import { createContext, useContext, useEffect } from "react";
import { fonts } from "@/config/fonts";
import { useLocalStorage } from "@/hooks/use-local-storage";

type Font = (typeof fonts)[number];

const FONT_STORAGE_NAME = "font";

type FontContextType = {
  font: Font;
  setFont: (font: Font) => void;
  resetFont: () => void;
};

const FontContext = createContext<FontContextType | null>(null);

export function FontProvider({ children }: { children: React.ReactNode }) {
  const [font, _setFont, removeFont] = useLocalStorage<Font>(FONT_STORAGE_NAME, "homeland");
  const activeFont = fonts.includes(font) ? font : "homeland";

  useEffect(() => {
    const applyFont = (font: string) => {
      const root = document.documentElement;
      root.classList.forEach((cls) => {
        if (cls.startsWith("font-")) root.classList.remove(cls);
      });
      root.classList.add(`font-${font}`);
    };
    applyFont(activeFont);
  }, [activeFont]);

  const setFont = (font: Font) => {
    _setFont(font);
  };

  const resetFont = () => {
    removeFont();
  };

  return <FontContext.Provider value={{ font: activeFont, setFont, resetFont }}>{children}</FontContext.Provider>;
}

export const useFont = () => {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error("useFont must be used within a FontProvider");
  }
  return context;
};
